// The hold the customer just created for THEMSELVES, remembered across a picker
// remount (browser back from /checkout is a client-side navigation — the component
// unmounts, the module state dies, but the 5-min DB hold stays live).
//
// Without this, the picker re-fetches availability and get_availability blocks the
// row on status IN ('hold','hold_paying') AND held_until > now() with no notion of
// WHOSE hold it is — so the customer's own hold makes their own slot look taken.
// Passing this booking id back as excludeBookingId nets it out for THIS browser
// only; every other browser still correctly sees the slot as held.
//
// sessionStorage (not localStorage) on purpose: the entry must die with the tab, so
// a stale hold from a previous session can never suppress a real block. Every access
// is try/catch'd — Safari private mode throws on access, and a booking flow must
// never break because storage is unavailable.

const KEY = "aluume_pending_hold";

export interface PendingHold {
  bookingId: string;
  resourceId: string;
  venue: string;
  /** ISO timestamptz of the slot start. */
  startAt: string;
  /** ISO timestamptz — when the hold lapses. */
  heldUntil: string;
}

function readRaw(): PendingHold | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<PendingHold>;
    if (!p?.bookingId || !p?.venue || !p?.heldUntil) return null;
    return p as PendingHold;
  } catch {
    return null;
  }
}

function writeRaw(entry: PendingHold | null): void {
  if (typeof window === "undefined") return;
  try {
    if (entry) window.sessionStorage.setItem(KEY, JSON.stringify(entry));
    else window.sessionStorage.removeItem(KEY);
  } catch {
    // Storage unavailable/full — the hold still exists server-side and the TTL +
    // sweep still frees it; we just lose the self-exclusion optimisation.
  }
}

/** Remember the hold this browser just created. One entry — a new hold replaces the old. */
export function writePendingHold(entry: PendingHold): void {
  writeRaw(entry);
}

/**
 * The live hold this browser owns at `venue`, or null.
 *
 * The expiry check is a client-side optimisation only: get_availability's own
 * `held_until > now()` predicate already makes an expired exclude id a harmless
 * no-op. Dropping it here just avoids sending an id we know is dead — and clears
 * the entry so it can't linger for the rest of the session.
 */
export function readPendingHold(venue: string): PendingHold | null {
  const p = readRaw();
  if (!p) return null;
  if (p.venue !== venue) return null;
  if (new Date(p.heldUntil).getTime() <= Date.now()) {
    clearPendingHold(p.bookingId);
    return null;
  }
  return p;
}

/**
 * Push out the stored expiry after the server extended the hold (the "Keep my space"
 * button, or start_payment's grace window at checkout init). Scoped to `bookingId` so
 * a late response for an abandoned hold can never resurrect it over a newer one.
 */
export function touchPendingHold(bookingId: string, heldUntil: string): void {
  const p = readRaw();
  if (!p || p.bookingId !== bookingId) return;
  writeRaw({ ...p, heldUntil });
}

/**
 * Forget the hold. Pass `bookingId` to clear it only if it's still the stored one
 * (the safe default — never wipe a newer hold); omit to clear unconditionally.
 */
export function clearPendingHold(bookingId?: string): void {
  if (!bookingId) { writeRaw(null); return; }
  const p = readRaw();
  if (!p || p.bookingId !== bookingId) return;
  writeRaw(null);
}
